/**
 * 個人ホームダッシュボード (Phase A / P-G)。
 *
 * 構成:
 *  1. 「自分の今日のタスク」(掲示板 / 個別説明予定 / 入金待ち / シリアル未入力 / 停滞案件)
 *     ※掲示板の 2 枚は useBoardAttention (onSnapshot) 由来でリアルタイム更新される。
 *  2. LINE Bot / メール生成 メトリクス (既存の MetricCard を維持)
 *
 * 設計方針: 営業の「今日やるべきこと」を1画面で確認できる導線を最上部に置き、
 * 大原則「直感的に理解できるプログラム」を体現する。
 *
 * 2026-10-30: タスクの行から商談詳細を開く時は戻り先 (ダッシュボード) を渡す。
 *   商談詳細の「前の画面へ」で、スクロール位置もそのままにここへ戻れる。
 */
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  CalendarCheck,
  Inbox,
  Cpu,
  Mail,
  MessageSquare,
  Package,
  Receipt,
  Sparkles,
  Star,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { EmptyState } from '@/components/common/EmptyState';
import { SetupWizard } from '@/components/common/SetupWizard';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/hooks/useAuth';
import { useBoardAttention } from '@/hooks/useBoardAttention';
import { useDashboardMetrics } from '@/hooks/useDashboardMetrics';
import { useDashboardTasks, type DashboardTasks } from '@/hooks/useDashboardTasks';
import { useReturnToState } from '@/hooks/useReturnTo';
import { DealStageBadge } from '@/pages/sales-contacts/components/DealStageBadge';
import { paths } from '@/routes/paths';

function MetricCard({
  label,
  value,
  color,
  suffix,
}: {
  label: string;
  value: number | string;
  color: string;
  suffix?: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-2xl md:text-3xl" style={{ color }}>
          {value}
          {suffix && (
            <span className="text-sm font-normal ml-1 text-[var(--text-muted)]">{suffix}</span>
          )}
        </CardTitle>
      </CardHeader>
    </Card>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const { data, isLoading } = useDashboardMetrics();
  const tasks = useDashboardTasks(user?.uid ?? null);
  // 掲示板は onSnapshot 由来なのでリアルタイムに件数が変わる。
  const board = useBoardAttention();
  const boardTotal = board.open + board.overdue + board.authorNews + board.threadNews;

  return (
    <Screen
      title={`おかえりなさい${user?.displayName ? '、' + user.displayName : ''}`}
      description="今日やるべきタスク + 全体メトリクス"
      restoreScroll
    >
      <SetupWizard />

      {/* 自分の今日のタスク */}
      <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-3 flex items-center gap-2">
        <Sparkles className="size-4 text-[var(--accent)]" />
        自分の今日のタスク
        {(tasks.data?.total ?? 0) + boardTotal > 0 && (
          <Badge variant="danger">{(tasks.data?.total ?? 0) + boardTotal} 件</Badge>
        )}
      </h2>

      {tasks.isLoading ? (
        <div className="mb-8">
          <Spinner />
        </div>
      ) : (tasks.data?.total ?? 0) === 0 && boardTotal === 0 ? (
        <Card className="mb-8">
          <EmptyState
            icon={Sparkles}
            title="今日のタスクは特にありません"
            description="掲示板の未対応や、新しい個別説明予定・入金待ちが発生したらここに表示されます。"
          />
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2 mb-8">
          {/* 掲示板 (2026-07-29 追加): 今日反応すべきものなので先頭に固定 */}
          {board.open + board.overdue > 0 && (
            <div>
              <BoardInboxCard open={board.open} overdue={board.overdue} unread={board.unread} />
            </div>
          )}
          {board.authorNews + board.threadNews > 0 && (
            <div>
              <BoardAuthoredCard authorNews={board.authorNews} threadNews={board.threadNews} />
            </div>
          )}
          {/* 件数 > 0 のカードを上 (左) に並べる */}
          {[
            {
              count: tasks.data?.briefings.length ?? 0,
              render: () => <BriefingCard tasks={tasks.data!} />,
            },
            {
              count: tasks.data?.invoices.length ?? 0,
              render: () => <InvoiceCard tasks={tasks.data!} />,
            },
            {
              count: tasks.data?.fulfillment.length ?? 0,
              render: () => <FulfillmentCard tasks={tasks.data!} />,
            },
            {
              count: tasks.data?.serials.length ?? 0,
              render: () => <SerialCard tasks={tasks.data!} />,
            },
            {
              count: tasks.data?.staleDeals.length ?? 0,
              render: () => <StaleDealCard tasks={tasks.data!} />,
            },
          ]
            .sort((a, b) => (b.count > 0 ? 1 : 0) - (a.count > 0 ? 1 : 0))
            .map((c, i) => (
              <div key={i}>{c.render()}</div>
            ))}
        </div>
      )}

      {/* LINE Bot セクション */}
      <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-3 flex items-center gap-2">
        <MessageSquare className="size-4" />
        LINE Bot
      </h2>
      <div className="grid gap-3 grid-cols-2 md:gap-4 md:grid-cols-[repeat(auto-fill,minmax(240px,1fr))] mb-8">
        <MetricCard
          label="今日の会話数"
          value={isLoading ? '—' : (data?.line.todayConversations ?? 0)}
          color="var(--accent)"
        />
        <MetricCard
          label="アクティブユーザー (30 日)"
          value={isLoading ? '—' : (data?.line.activeUsers30d ?? 0)}
          color="var(--accent-cyan)"
        />
        <MetricCard
          label="平均評価 (7 日)"
          value={isLoading ? '—' : (data?.line.avgRating7d ?? 0)}
          color="var(--accent-green)"
          suffix="/ 5"
        />
        <MetricCard
          label="要レビュー QA"
          value={isLoading ? '—' : (data?.line.lowRatingUnreviewed ?? 0)}
          color="var(--accent-amber)"
        />
      </div>

      {/* Mail セクション */}
      <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-3 flex items-center gap-2">
        <Mail className="size-4" />
        メール生成
      </h2>
      <div className="grid gap-3 grid-cols-2 md:gap-4 md:grid-cols-[repeat(auto-fill,minmax(240px,1fr))] mb-8">
        <MetricCard
          label="今月の生成"
          value={isLoading ? '—' : (data?.mail.generatedThisMonth ?? 0)}
          color="var(--accent)"
        />
        <MetricCard
          label="今月の送信"
          value={isLoading ? '—' : (data?.mail.sentThisMonth ?? 0)}
          color="var(--accent-cyan)"
        />
        <MetricCard
          label="承認待ち"
          value={isLoading ? '—' : (data?.mail.pendingReview ?? 0)}
          color="var(--accent-purple)"
        />
        <MetricCard
          label="下書き"
          value={isLoading ? '—' : (data?.mail.drafts ?? 0)}
          color="var(--accent-amber)"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="min-h-56">
          <EmptyState
            icon={Users}
            title="時系列グラフ"
            description="日別会話数 / 問合せタイプ別分布は集計バッチと Recharts で別途実装予定"
          />
        </Card>
        <Card className="min-h-56">
          <EmptyState
            icon={Star}
            title="評価分布"
            description="rating 分布 / テンプレート別利用率グラフは今後拡張"
          />
        </Card>
      </div>

      {isLoading && (
        <div className="flex justify-center mt-8">
          <Spinner />
        </div>
      )}
    </Screen>
  );
}

// ─────────────────────────────────────────────
// タスクカード群
// ─────────────────────────────────────────────

function TaskCard({
  title,
  icon: Icon,
  count,
  color,
  empty,
  children,
}: {
  title: string;
  icon: LucideIcon;
  count: number;
  color: string;
  empty: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Icon className="size-4" style={{ color }} />
          {title}
        </h3>
        {count > 0 ? (
          <Badge variant="danger">{count}</Badge>
        ) : (
          <span className="text-xs text-[var(--text-muted)]">0</span>
        )}
      </div>
      {count === 0 ? (
        <p className="text-xs text-[var(--text-muted)] italic">{empty}</p>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">{children}</div>
      )}
    </div>
  );
}

function TaskRow({
  to,
  primary,
  secondary,
  badge,
}: {
  to: string;
  primary: string;
  secondary?: string;
  badge?: React.ReactNode;
}) {
  const returnToState = useReturnToState('ダッシュボード');
  return (
    <Link
      to={to}
      state={returnToState}
      className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] transition-colors"
    >
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-[var(--text-primary)] truncate">{primary}</div>
        {secondary && (
          <div className="text-[10px] text-[var(--text-muted)] mt-0.5 truncate">{secondary}</div>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {badge}
        <ArrowRight className="size-3 text-[var(--text-muted)]" />
      </div>
    </Link>
  );
}

function fmtTime(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * 掲示板「あなたが対応する番」(2026-07-29 追加)。
 * 掲示板一覧の「あなた宛」タブへ直接着地させる。
 */
function BoardInboxCard({
  open,
  overdue,
  unread,
}: {
  open: number;
  overdue: number;
  unread: number;
}) {
  return (
    <Card className="border-l-2 border-l-[var(--accent)]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Inbox className="size-4 text-[var(--accent)]" />
          掲示板 — あなたが対応する番
          <Badge variant="danger">{open} 件</Badge>
        </CardTitle>
        <CardDescription>
          {overdue > 0 && (
            <span className="text-[var(--accent-red)] font-semibold">期日超過 {overdue} 件</span>
          )}
          {overdue > 0 && unread > 0 && ' ・ '}
          {unread > 0 && <span>未確認 {unread} 件</span>}
          {overdue === 0 && unread === 0 && '対応をお待ちしています'}
        </CardDescription>
      </CardHeader>
      <div className="px-6 pb-4">
        <Link
          to={paths.board}
          className="text-xs text-[var(--accent)] hover:underline inline-flex items-center gap-1"
        >
          掲示板で確認する <ArrowRight className="size-3" />
        </Link>
      </div>
    </Card>
  );
}

/**
 * 掲示板「あなたの投稿の動き」(2026-07-29 追加)。
 * 相手が対応完了にした投稿 / スレッド返信を拾う。
 */
function BoardAuthoredCard({ authorNews, threadNews }: { authorNews: number; threadNews: number }) {
  return (
    <Card className="border-l-2 border-l-[var(--accent-purple)]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Bell className="size-4 text-[var(--accent-purple)]" />
          掲示板 — あなたの投稿の動き
          <Badge variant="review">{authorNews + threadNews} 件</Badge>
        </CardTitle>
        <CardDescription>
          {authorNews > 0 && <span>相手が対応完了 {authorNews} 件</span>}
          {authorNews > 0 && threadNews > 0 && ' ・ '}
          {threadNews > 0 && <span>💬 スレッド返信 {threadNews} 件</span>}
        </CardDescription>
      </CardHeader>
      <div className="px-6 pb-4">
        <Link
          to={paths.board}
          className="text-xs text-[var(--accent-purple)] hover:underline inline-flex items-center gap-1"
        >
          確認してクローズする <ArrowRight className="size-3" />
        </Link>
      </div>
    </Card>
  );
}

function BriefingCard({ tasks }: { tasks: DashboardTasks }) {
  return (
    <TaskCard
      title="個別説明 予定"
      icon={CalendarCheck}
      count={tasks.briefings.length}
      color="var(--accent)"
      empty="今日〜7日以内の個別説明予定はありません"
    >
      {tasks.briefings.map((b) => (
        <TaskRow
          key={b.dealId}
          to={paths.dealDetail.replace(':id', b.dealId)}
          primary={b.dealTitle}
          secondary={fmtTime(b.scheduledAt)}
          badge={
            b.isToday ? <Badge variant="danger">今日</Badge> : <Badge variant="default">予定</Badge>
          }
        />
      ))}
    </TaskCard>
  );
}

function InvoiceCard({ tasks }: { tasks: DashboardTasks }) {
  return (
    <TaskCard
      title="入金待ち"
      icon={Receipt}
      count={tasks.invoices.length}
      color="var(--accent-amber)"
      empty="未入金の請求はありません"
    >
      {tasks.invoices.map((i) => (
        <TaskRow
          key={i.invoiceId}
          to={paths.dealDetail.replace(':id', i.dealId)}
          primary={i.invoiceNumber}
          secondary={`¥${i.amount.toLocaleString('ja-JP')}${i.dueDate ? ` · 期限 ${i.dueDate.toLocaleDateString('ja-JP')}` : ''}`}
          badge={
            i.isOverdue ? (
              <Badge variant="danger">期限超過</Badge>
            ) : (
              <Badge variant="draft">未入金</Badge>
            )
          }
        />
      ))}
    </TaskCard>
  );
}

const FULFILLMENT_ACTION = {
  ordered: { label: '入荷を記録する', badge: '入荷待ち', variant: 'sent' },
  received: { label: '検品・シリアルを入力する', badge: '検品待ち', variant: 'review' },
  inspected: { label: '顧客へ発送する', badge: '発送待ち', variant: 'approved' },
} as const;

function FulfillmentCard({ tasks }: { tasks: DashboardTasks }) {
  return (
    <TaskCard
      title="入荷・検品・発送"
      icon={Package}
      count={tasks.fulfillment.length}
      color="var(--accent-purple)"
      empty="入荷・検品・発送の待ちはありません"
    >
      {tasks.fulfillment.map((f) => {
        const a = FULFILLMENT_ACTION[f.status];
        return (
          <TaskRow
            key={f.poId}
            to={`${paths.dealDetail.replace(':id', f.dealId)}?tab=fulfillment&po=${f.poId}`}
            primary={a.label}
            secondary={`${f.dealTitle} · ${f.poNumber} · ${f.detail}`}
            badge={<Badge variant={a.variant}>{a.badge}</Badge>}
          />
        );
      })}
    </TaskCard>
  );
}

function SerialCard({ tasks }: { tasks: DashboardTasks }) {
  return (
    <TaskCard
      title="シリアル未入力"
      icon={Cpu}
      count={tasks.serials.length}
      color="var(--accent-cyan)"
      empty="シリアル未入力の発送はありません"
    >
      {tasks.serials.map((s) => (
        <TaskRow
          key={s.deliveryId}
          to={paths.dealDetail.replace(':id', s.dealId)}
          primary={s.deliveryNumber}
          secondary={`シリアル: ${s.filledSerials}/${s.totalSerials}`}
          badge={<Badge variant="draft">入力</Badge>}
        />
      ))}
    </TaskCard>
  );
}

function StaleDealCard({ tasks }: { tasks: DashboardTasks }) {
  return (
    <TaskCard
      title="停滞案件 (7日超)"
      icon={AlertTriangle}
      count={tasks.staleDeals.length}
      color="var(--accent-red)"
      empty="停滞中の案件はありません"
    >
      {tasks.staleDeals.map((d) => (
        <TaskRow
          key={d.dealId}
          to={paths.dealDetail.replace(':id', d.dealId)}
          primary={d.title}
          secondary={`${d.daysSinceTouch} 日経過`}
          badge={<DealStageBadge stage={d.stage} />}
        />
      ))}
    </TaskCard>
  );
}
